import apiClient from './api';
import { Address, ApiResponse } from '../types/api';
import { toast } from 'react-hot-toast';
import { analyticsService } from './analytics';

export interface AddressWithId extends Address {
  id: string;
  isDefault?: boolean;
}

const addressService = {
  /**
   * Get all addresses for the current user
   */
  getAddresses: async (): Promise<AddressWithId[]> => {
    try {
      // Skip fetching addresses on non-authenticated pages
      const isNonAuthPage = 
        window.location.pathname.includes('/login') ||
        window.location.pathname.includes('/register') ||
        window.location.pathname.includes('/reset-password') ||
        window.location.pathname.includes('/verify-email') ||
        window.location.pathname.includes('/forgot-password') ||
        window.location.pathname === '/' ||
        window.location.pathname.includes('/welcome') ||
        window.location.pathname.includes('/flowers');
      
      // Check both non-auth page condition and if token exists
      if (isNonAuthPage || !localStorage.getItem('authToken')) {
        console.log('addressService: Skipping fetch on non-authenticated page or missing token:', window.location.pathname);
        return [];
      }
      
      const response = await apiClient.get<ApiResponse<AddressWithId[]>>('/addresses');
      return response.data.data;
    } catch (error: any) {
      console.error('Get addresses error:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // Instead of letting API interceptor handle it which causes a redirect,
        // just return empty array for the Flowers page
        if (window.location.pathname.includes('/flowers')) {
          console.log('addressService: Ignoring 401 error on Flowers page');
          return [];
        }
        
        // Otherwise let the API interceptor handle it
        throw error;
      }
      
      // For other errors, we can show a toast
      toast.error('Could not load your addresses. Please try again later.');
      return [];
    }
  },
  
  /**
   * Get a specific address by ID
   */
  getAddressById: async (addressId: string): Promise<AddressWithId | null> => {
    try {
      const response = await apiClient.get<ApiResponse<AddressWithId>>(`/addresses/${addressId}`);
      return response.data.data;
    } catch (error) {
      console.error(`Get address ${addressId} error:`, error);
      return null;
    }
  },
  
  /**
   * Create a new address
   */
  createAddress: async (address: Address): Promise<AddressWithId | null> => {
    try {
      const response = await apiClient.post<ApiResponse<AddressWithId>>('/addresses', address);
      const createdAddress = response.data.data;
      
      if (createdAddress) {
        // Track address creation with Google Analytics
        try {
          analyticsService.trackAddressCreation(
            address.addressLabel?.toLowerCase() || 'home',
            createdAddress.isDefault || false
          );
        } catch (analyticsError) {
          console.error('Error tracking address creation:', analyticsError);
          // Don't block the flow if analytics tracking fails
        }
      }
      
      return createdAddress;
    } catch (error) {
      console.error('Create address error:', error);
      return null;
    }
  },
  
  /**
   * Update an existing address
   */
  updateAddress: async (addressId: string, address: Partial<Address>): Promise<AddressWithId | null> => {
    try {
      const response = await apiClient.put<ApiResponse<AddressWithId>>(`/addresses/${addressId}`, address);
      return response.data.data;
    } catch (error) {
      console.error(`Update address ${addressId} error:`, error);
      return null;
    }
  },
  
  /**
   * Delete an address
   */
  deleteAddress: async (addressId: string): Promise<boolean> => {
    try {
      console.log(`[AddressService] Deleting address with ID: ${addressId}`);
      // Log auth token (partially masked)
      const token = localStorage.getItem('authToken');
      if (token) {
        const maskedToken = `${token.substring(0, 10)}...${token.substring(token.length - 10)}`;
        console.log(`[AddressService] Using auth token: ${maskedToken}`);
      } else {
        console.warn('[AddressService] No auth token found in localStorage');
      }
      
      const response = await apiClient.delete(`/addresses/${addressId}`);
      console.log(`[AddressService] Delete address response:`, response);
      return true;
    } catch (error: any) {
      console.error(`[AddressService] Delete address ${addressId} error:`, error);
      // Add more detailed error logging
      if (error.response) {
        console.error(`[AddressService] Error status: ${error.response.status}`);
        console.error(`[AddressService] Error data:`, error.response.data);
      }
      return false;
    }
  },
  
  /**
   * Set an address as default
   */
  setDefaultAddress: async (addressId: string): Promise<boolean> => {
    try {
      await apiClient.put(`/addresses/${addressId}/default`);
      return true;
    } catch (error) {
      console.error(`Set default address ${addressId} error:`, error);
      return false;
    }
  }
};

export default addressService; 